import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import WishGridItem from "../../wishes/display-wishes/wish-grid/wish-grid-item/WishGridItem";
import {EMPTY_WISHES, getRandomWishesArray, WishSortOptions, WishStatus, WishType} from "../../../store/wish/Helper";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import * as React from "react";
import {useEffect, useMemo, useRef} from "react";
import {getWishes} from "../../../store/wish/ActionCreators";
import useWindowDimensions from "../../../util/hooks/useWindowDimensions";
import ReactGA from "react-ga4";
import {Autoplay, Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import IconButton from "@mui/material/IconButton";
import {ArrowBackIosRounded, ArrowForwardIosRounded, ExpandCircleDownOutlined} from "@mui/icons-material";
import {Swiper as SwiperType} from "swiper";
import WishGridItemSkeleton from "../../wishes/display-wishes/wish-grid/wish-grid-item-skeleton/WishGridItemSkeleton";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../util/constants";

const HOME_TRENDING_VOUCHERS_KEY = "home-trending-vouchers"

const TrendingVouchers = () => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const trendingWishesLoading = useAppSelector((state: RootState) => state.wishReducer.fetchWishesLoadingByKey[HOME_TRENDING_VOUCHERS_KEY] ?? false)
    const trendingWishes = useAppSelector((state: RootState) => state.wishReducer.itemsByKey[HOME_TRENDING_VOUCHERS_KEY]?.data ?? EMPTY_WISHES)
    const {width} = useWindowDimensions()
    const swiperRef = useRef<SwiperType>(undefined)

    const numberOfWishes = useMemo(() => {
        let newNumber
        if (!width) return 1
        if (width < 600) newNumber = 1
        else if (width < 900) newNumber = 2
        else if (width < 1200) newNumber = 3
        else newNumber = 4

        if (newNumber > trendingWishes.length && !!trendingWishes.length)
            newNumber = trendingWishes.length

        return newNumber
    }, [width, trendingWishes.length])

    const shouldShowNavigation = trendingWishes.length > numberOfWishes;

    const handleOpenWishDetails = (wishId: string) => {
        ReactGA.event("select_item", {items: [{item_id: wishId, item_name: 'Wish Voucher - Trending'}]})
    }

    useEffect(() => {
        dispatch(getWishes({
            category_id: undefined,
            sorting: `${WishSortOptions.END_TIME},asc`,
            page: 0,
            searchText: "",
            status: WishStatus.ACTIVE,
            limit: 8,
            loadMore: false,
            recommendedFirst: false,
            type: WishType.VOUCHER,
            excludeFree: true,
            key: HOME_TRENDING_VOUCHERS_KEY,
        }))
    }, [dispatch])

    return (
        trendingWishesLoading || trendingWishes.length !== 0 ? <Box mt={12}>
            <Typography
                variant="h4"
                color="primary"
                fontWeight={600}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                }}
            >
                {t('partner_vouchers')}
                <IconButton
                    color="primary"
                    size="small"
                    component={RouterLink}
                    to={`${RoutePaths.WISHES}?type=voucher&sort=tickets_count,desc`}
                    aria-label={t('view_all_vouchers')}
                    sx={{ml: 1}}
                >
                    <ExpandCircleDownOutlined sx={{transform: 'rotate(-90deg)'}}/>
                </IconButton>
            </Typography>
            <Typography
                variant="body1"
                color="primary"
                sx={{mt: 0.75, opacity: 0.9}}
            >
                {t('partner_vouchers_subtitle')}
            </Typography>

            <Swiper
                onSwiper={(swiper: SwiperType) => {
                    // @ts-ignore
                    swiperRef.current = swiper
                }}
                modules={[Navigation, Autoplay]}
                spaceBetween={20}
                slidesPerView={numberOfWishes}
                navigation={shouldShowNavigation ? {
                    nextEl: '.swiper-button-next-trending',
                    prevEl: '.swiper-button-prev-trending',
                } : false}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }}
                cssMode
                rewind={shouldShowNavigation}
                onSlideChange={(swiper: SwiperType) => {
                    swiper?.autoplay?.start()
                }}
                style={{marginTop: "1em"}}
            >
                {(trendingWishesLoading ? getRandomWishesArray() : trendingWishes).map(w => (
                    <SwiperSlide key={w.id} style={{display: 'flex', justifyContent: 'center', height: 'auto'}}>
                        {trendingWishesLoading ? <WishGridItemSkeleton/> : <WishGridItem wish={w}
                                                                                         swiperInstance={swiperRef.current}
                                                                                         handleOpenWishDetails={() => handleOpenWishDetails(w.id)}/>}
                    </SwiperSlide>
                ))}
                {shouldShowNavigation && (
                    <>
                        <IconButton color="primary" className="swiper-button-prev-trending" aria-label="previous" sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10
                        }}>
                            <ArrowBackIosRounded/>
                        </IconButton>
                        <IconButton color="primary" className="swiper-button-next-trending" aria-label="next" sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10
                        }}>
                            <ArrowForwardIosRounded/>
                        </IconButton>
                    </>
                )}
            </Swiper>
        </Box> : null
    )
}

export default TrendingVouchers
